import {
  LitheElement,
  LitheFC,
  LitheSpecificElement,
} from '@wheelbox/lithejs-engine';
import withTranslations from '@wheelbox/lithejs-i18n';

import LoadingIndicator from '@components/layout/loading-indicator';
import DocsLandingView from './landing';

import { createId } from '@utils/index';

import { LoadingIndicatorSize } from '@components/layout/loading-indicator/types';
import { type DocData, type DocMeta } from '@lib/docs-loader';

type DocsContentViewProps = {
  data: DocData | null;
  sections: DocMeta[];
  previous?: DocMeta;
  next?: DocMeta;
  isLoading: boolean;
  classes?: string[] | string;
  onselect: (target: string) => void;
};

const DocsContentView: LitheFC<DocsContentViewProps> = ({
  id = createId(),
  testId = 'docs-content',
  data,
  sections,
  previous,
  next,
  isLoading,
  onselect,
}) => {
  const t = withTranslations('Docs');

  const renderTitle = (): LitheElement<'h1'> | null => {
    if (!data) return null;

    return [
      'h1',
      {
        class: 'text-3xl text-primary flex items-center mb-6',
      },
      data.title ?? '',
    ];
  };

  const renderContent = (): LitheSpecificElement => {
    if (!data?.content) {
      return ['h3', t('no_content')];
    }

    return [
      'div',
      {
        class: [
          'prose prose-slate dark:prose-invert max-w-none',
          // Headings
          '[&_h2]:text-2xl [&_h2]:text-primary [&_h2]:mt-8 [&_h2]:mb-4',
          '[&_h3]:text-xl [&_h3]:text-foreground [&_h3]:mt-6 [&_h3]:mb-3',
          // Code blocks
          '[&_pre]:bg-slate-900 [&_pre]:text-slate-50 [&_pre]:p-4 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:text-sm [&_pre]:leading-relaxed',
          '[&_pre]:border [&_pre]:border-slate-700',
          // Inline code
          '[&_code]:bg-primary/10 [&_code]:text-primary [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono',
          '[&_pre_code]:bg-transparent [&_pre_code]:text-inherit [&_pre_code]:p-0',
          // Links
          '[&_a]:text-primary [&_a]:underline hover:[&_a]:text-primary/80',
          // Lists
          '[&_ul]:my-4 [&_ul]:pl-6 [&_li]:my-1',
          '[&_ol]:my-4 [&_ol]:pl-6',
          // Paragraphs
          '[&_p]:my-4 [&_p]:leading-7',
          // Tables
          '[&_table]:w-full [&_table]:border-collapse [&_table]:my-6',
          '[&_th]:border [&_th]:border-border [&_th]:px-4 [&_th]:py-2 [&_th]:bg-muted [&_th]:text-left',
          '[&_td]:border [&_td]:border-border [&_td]:px-4 [&_td]:py-2',
        ].join(' '),
      },
      data.content,
    ];
  };

  const renderNavigation = (): LitheElement<'div'> | null => {
    if (!previous && !next) return null;

    return [
      'div',
      {
        class: [
          'flex flex-col md:flex-row mt-12 mb-4 gap-4',
          !previous ? 'justify-end' : 'justify-between',
        ],
      },
      ...(previous
        ? [
            [
              'button',
              {
                class:
                  'flex flex-col items-start px-4 py-2 rounded-md hover:bg-muted transition-colors',
                onclick: () => onselect(previous.filename),
              },
              [
                'span',
                { class: 'text-sm text-muted-foreground' },
                t('previous'),
              ],
              ['span', { class: 'text-primary' }, previous.title],
            ] as LitheElement<'button'>,
          ]
        : []),
      ...(next
        ? [
            [
              'button',
              {
                class:
                  'flex flex-col items-end px-4 py-2 rounded-md hover:bg-muted transition-colors ml-auto',
                onclick: () => onselect(next.filename),
              },
              ['span', { class: 'text-sm text-muted-foreground' }, t('next')],
              ['span', { class: 'text-primary' }, next.title],
            ] as LitheElement<'button'>,
          ]
        : []),
    ];
  };

  const renderLoading = () =>
    LoadingIndicator({ size: LoadingIndicatorSize.LARGE });

  const renderLanding = (): LitheSpecificElement =>
    DocsLandingView({ sections, onselect });

  const renderLoaded = (): LitheSpecificElement[] => {
    const elements: LitheSpecificElement[] = [];

    const title = renderTitle();
    if (title) elements.push(title);

    elements.push(renderContent());

    const navigation = renderNavigation();
    if (navigation) elements.push(navigation);

    return elements;
  };

  // Show landing page when no doc is selected (data is null and not loading)
  const showLanding = !data && !isLoading;

  return [
    'div',
    {
      id,
      'data-testid': testId,
      class: [
        'docs-content',
        isLoading
          ? 'flex min-h-[400px] w-full flex-col justify-center items-center'
          : '',
      ],
    },
    ...(showLanding
      ? [renderLanding()]
      : isLoading
      ? [renderLoading()]
      : renderLoaded()),
  ];
};

export default DocsContentView;
